import { makeStyles } from "@material-ui/core";
import GearBg from "assets/img/gearwave.png";
import colors from "style/colors";

export const useStyles = makeStyles((theme) => ({
  root: {
    background: `center / cover no-repeat url(${GearBg})`,
    backgroundAttachment: "fixed",
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  container: {
    width: 640,
    maxWidth: "95%",
    display: "flex",
    flexDirection: "column",
    padding: 24,
    [theme.breakpoints.down("xs")]: {
      padding: 16,
    },
  },
  card: {
    padding: 32,
    [theme.breakpoints.down("xs")]: {
      padding: 20,
    },
  },
  logoRow: {
    marginBottom: 24,
  },
  title: {
    fontSize: "1.75rem",
    fontWeight: 600,
    marginBottom: 12,
    color: colors.highEmphasis,
  },
  body: {
    fontSize: "1rem",
    lineHeight: 1.6,
    color: colors.mediumEmphasis,
    marginBottom: 24,
  },
  walletSection: {
    marginBottom: 32,
    padding: 16,
    backgroundColor: colors.grey100,
    borderRadius: 8,
  },
  walletLabel: {
    fontSize: "0.875rem",
    fontWeight: 600,
    marginBottom: 8,
    color: colors.highEmphasis,
  },
  address: {
    fontFamily: "monospace",
    fontSize: "0.875rem",
    wordBreak: "break-all",
    color: colors.greyscale,
  },
  addressLink: {
    color: colors.linkBlue,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  walletConnectedRow: {
    display: "flex",
    flexWrap: "wrap" as const,
    alignItems: "center",
    gap: 4,
  },
  disconnectButton: {
    background: "none",
    border: "none",
    padding: 0,
    font: "inherit",
    color: colors.linkBlue,
    cursor: "pointer",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  walletOptionalHint: {
    marginBottom: 12,
    color: colors.mediumEmphasis,
    fontSize: "0.875rem",
  },
  stepsTitle: {
    fontSize: "1.125rem",
    fontWeight: 600,
    marginBottom: 16,
    color: colors.highEmphasis,
  },
  step: {
    marginBottom: 16,
    paddingLeft: 8,
    borderLeft: `3px solid ${colors.linkBlue}`,
  },
  stepNumber: {
    fontWeight: 600,
    fontSize: "0.875rem",
    color: colors.highEmphasis,
    marginBottom: 4,
  },
  stepText: {
    fontSize: "0.9375rem",
    lineHeight: 1.5,
    color: colors.mediumEmphasis,
  },
  code: {
    fontFamily: "monospace",
    fontSize: "0.8125rem",
    backgroundColor: colors.black5,
    padding: "2px 6px",
    borderRadius: 4,
    display: "inline-block",
    marginTop: 4,
  },
  stepImagesWrap: {
    marginTop: 16,
    display: "flex",
    flexDirection: "column" as const,
    gap: 20,
  },
  stepImageBlock: {
    margin: 0,
    maxWidth: "100%",
  },
  stepImage: {
    display: "block",
    maxWidth: "100%",
    height: "auto",
    borderRadius: 8,
    border: `1px solid ${colors.black23}`,
  },
  stepImageCaption: {
    marginTop: 6,
    fontSize: "0.8125rem",
    color: colors.mediumEmphasis,
    lineHeight: 1.4,
  },
  positionsSection: {
    marginTop: 24,
    marginBottom: 24,
  },
  positionsTitle: {
    fontSize: "1.125rem",
    fontWeight: 600,
    marginBottom: 16,
    color: colors.highEmphasis,
  },
  positionCard: {
    padding: 16,
    marginBottom: 12,
    backgroundColor: colors.grey100,
    borderRadius: 8,
    border: `1px solid ${colors.black23}`,
  },
  positionRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
    flexWrap: "wrap" as const,
    gap: 8,
  },
  positionLabel: {
    fontSize: "0.875rem",
    color: colors.mediumEmphasis,
  },
  positionValue: {
    fontSize: "0.875rem",
    fontWeight: 600,
    color: colors.highEmphasis,
  },
  positionActions: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: 8,
    marginTop: 12,
  },
  positionButton: {
    minWidth: 140,
  },
  positionsLoading: {
    padding: 24,
    textAlign: "center" as const,
    color: colors.mediumEmphasis,
  },
  positionsEmpty: {
    padding: 16,
    textAlign: "center" as const,
    color: colors.mediumEmphasis,
    fontSize: "0.9375rem",
  },
  subsectionTitle: {
    fontSize: "1rem",
    fontWeight: 600,
    marginTop: 24,
    marginBottom: 12,
    color: colors.highEmphasis,
  },
  chainList: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    "& li": {
      marginBottom: 8,
    },
  },
  chainLink: {
    color: colors.linkBlue,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  allPoolsSection: {
    marginTop: 32,
    paddingTop: 24,
    borderTop: `1px solid ${colors.black23}`,
  },
  poolListByChain: {
    marginBottom: 24,
    paddingBottom: 24,
    borderBottom: `1px solid ${colors.black23}`,
    "&:last-child": {
      borderBottom: "none",
      marginBottom: 0,
      paddingBottom: 0,
    },
  },
  poolListLabel: {
    fontSize: "1rem",
    fontWeight: 700,
    marginBottom: 12,
    color: colors.highEmphasis,
  },
  poolTestName: {
    fontSize: "0.875rem",
    fontWeight: 400,
    color: colors.mediumEmphasis,
    marginBottom: 2,
  },
  poolAddressRow: {
    fontSize: "0.8125rem",
    fontFamily: "monospace",
    marginBottom: 12,
    wordBreak: "break-all" as const,
  },
  poolAddressLink: {
    color: colors.linkBlue,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },

  migrationCard: {
    padding: 20,
    marginBottom: 12,
    borderRadius: 8,
    border: `1px solid rgba(71, 196, 153, 0.3)`,
    background:
      "linear-gradient(135deg, rgba(71, 196, 153, 0.06), rgba(33, 91, 196, 0.06))",
  },
  migrationCardHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 16,
  },
  migrationCheckIcon: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: colors.success,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: colors.white,
    fontSize: "1rem",
    fontWeight: 700,
  },
  migrationCardTitle: {
    fontSize: "1rem",
    fontWeight: 600,
    color: colors.highEmphasis,
    lineHeight: 1.3,
  },
  migrationCardBody: {
    fontSize: "0.9375rem",
    lineHeight: 1.6,
    color: colors.mediumEmphasis,
    marginBottom: 16,
  },
  migrationCardSubtitle: {
    fontSize: "0.875rem",
    fontWeight: 600,
    color: colors.highEmphasis,
    marginBottom: 8,
  },
  migrationSteps: {
    listStyle: "none",
    margin: "0 0 20px",
    padding: 0,
    display: "flex",
    flexDirection: "column" as const,
    gap: 6,
    width: "100%",
    textAlign: "left" as const,
  },
  migrationStep: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    fontSize: "0.875rem",
    lineHeight: 1.5,
    color: colors.mediumEmphasis,
    "& > span:last-of-type": {
      flex: 1,
      minWidth: 0,
      textAlign: "left" as const,
    },
  },
  migrationStepNumber: {
    fontWeight: 600,
    color: colors.highEmphasis,
    flexShrink: 0,
    minWidth: "1.25em",
    lineHeight: 1.5,
    paddingTop: "0.125em",
  },
  migrationCta: {
    display: "inline-block",
    padding: "12px 24px",
    borderRadius: 8,
    background: "linear-gradient(135deg, #47C499, #215BC4)",
    color: colors.white,
    fontSize: "0.875rem",
    fontWeight: 600,
    textDecoration: "none",
    textAlign: "center" as const,
    transition: "opacity 0.2s",
    "&:hover": {
      opacity: 0.9,
    },
  },
  migrationPoolName: {
    fontSize: "0.8125rem",
    color: colors.mediumEmphasis,
    marginBottom: 4,
  },

  allClearedCard: {
    padding: 28,
    marginBottom: 12,
    borderRadius: 8,
    border: `1px solid rgba(71, 196, 153, 0.3)`,
    background:
      "linear-gradient(135deg, rgba(71, 196, 153, 0.08), rgba(33, 91, 196, 0.08))",
    textAlign: "center" as const,
  },
  allClearedTitle: {
    fontSize: "1.25rem",
    fontWeight: 600,
    color: colors.highEmphasis,
    marginBottom: 8,
  },
  allClearedBody: {
    fontSize: "0.9375rem",
    lineHeight: 1.6,
    color: colors.mediumEmphasis,
    marginBottom: 20,
    maxWidth: 440,
    marginLeft: "auto",
    marginRight: "auto",
  },
}));
